import React, { useCallback, useMemo } from 'react'
import { Box, Paper, Typography, Button, CircularProgress, Alert, Stack, ListItemText } from '@mui/material'
import { FormTemplate, GetMeetingLinkQuery, FormField as IFormField } from '@/core/graphql/types'
import FormField from './FormField'
import { useTranslations } from '@/lib/next-shim'

type FormFieldValue = string | number | boolean | string[]
export type FormDataRecord = Record<string, FormFieldValue>

export type DynamicFormProps = {
  template: Pick<FormTemplate, '_id' | 'title' | 'description' | 'fields'>
  formData: FormDataRecord
  validationErrors: Record<string, string>
  isSubmitting: boolean
  onFormChange: (data: FormDataRecord) => void
  onSubmit: (data: FormDataRecord) => void | Promise<void>
}

const DynamicForm: React.FC<DynamicFormProps> = ({ template, formData, validationErrors, isSubmitting, onFormChange, onSubmit }) => {
  const t = useTranslations()

  const handleFieldChange = useCallback(
    (fieldId: string) => (value: FormFieldValue) => {
      onFormChange({ ...formData, [fieldId]: value })
    },
    [formData, onFormChange]
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const validationErrorsList = useMemo(() => {
    return Object.entries(validationErrors).map(([fieldId, error]) => {
      const field = template?.fields.find((f) => f.id === fieldId)
      return {
        key: fieldId,
        label: field?.label || fieldId,
        error,
      }
    })
  }, [validationErrors, template?.fields])

  return (
    <div>
      <Box sx={{ mb: 2 }}>
        <ListItemText primary={template.title} secondary={template.description} />
      </Box>

      {validationErrorsList.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">
            <Typography variant="subtitle2" gutterBottom>
              {t('form.validation.fixErrors')}
            </Typography>
            <ul>
              {validationErrorsList.map(({ key, label, error }) => (
                <li key={key}>
                  {label}: {error}
                </li>
              ))}
            </ul>
          </Alert>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {template.fields.map((field: IFormField) => (
            <FormField key={field.id} field={field} value={formData[field.id]} error={validationErrors[field.id]} onChange={handleFieldChange(field.id)} />
          ))}
        </Stack>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="contained" color="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('form.submission.submitting')}
              </>
            ) : (
              t('form.submission.submit')
            )}
          </Button>
        </Box>
      </form>
    </div>
  )
}

export default React.memo(DynamicForm)
